import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '../services/api';
import { useAuth } from './AuthContext';

interface FeatureFlags {
  // Online food ordering (browse menu + cart + place order). Defaults OFF until
  // an admin enables it in System Settings. Scan & Pay is NOT gated by this.
  foodOrderingEnabled: boolean;
  loading: boolean;
  refresh: () => void;
}

const FeatureFlagContext = createContext<FeatureFlags>({
  foodOrderingEnabled: false,
  loading: true,
  refresh: () => {},
});

export const FeatureFlagProvider = ({ children }: { children: React.ReactNode }) => {
  const { role } = useAuth();
  const [foodOrderingEnabled, setFoodOrderingEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    // Only authenticated users can read settings; default to OFF otherwise.
    if (!role) {
      setFoodOrderingEnabled(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.settings.get('food_ordering_enabled');
      setFoodOrderingEnabled(data?.setting_value === 'true');
    } catch {
      // Missing key / network error → treat as disabled (safe default).
      setFoodOrderingEnabled(false);
    } finally {
      setLoading(false);
    }
  }, [role]);

  useEffect(() => { load(); }, [load]);

  return (
    <FeatureFlagContext.Provider value={{ foodOrderingEnabled, loading, refresh: load }}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlags = () => useContext(FeatureFlagContext);
